/* Church Sign Shop — minimal print-PDF writer (no dependencies).
   Builds a press-ready PDF: each page is trim size + bleed on every side,
   with the rendered artwork embedded as a JPEG (DCTDecode), the CNC anchor
   marks drawn as vectors on top, and crop ticks at the trim corners.

   Anchor-mark geometry replicates the shop's production file
   (Standard_Greeting_22x22_ForExport.pdf): a paper-white square with a
   black dot centered in it, at fixed sheet positions the CNC camera scans.
   MediaBox = bleed box, TrimBox = finished sign size.
   Exposes window.SignPDF.build().                                          */
(function () {
  "use strict";

  var PT = 72; /* PDF points per inch */
  var K = 0.552284749831; /* Bézier circle constant */

  function enc(str) {
    var u = new Uint8Array(str.length);
    for (var i = 0; i < str.length; i++) u[i] = str.charCodeAt(i) & 0xff;
    return u;
  }
  function num(n) {
    return (Math.round(n * 100) / 100).toString();
  }

  /* Vector circle path ops (filled). cx/cy/r in points. */
  function circleOps(cx, cy, r) {
    var k = K * r;
    return [
      num(cx + r) + " " + num(cy) + " m",
      num(cx + r) + " " + num(cy + k) + " " + num(cx + k) + " " + num(cy + r) + " " + num(cx) + " " + num(cy + r) + " c",
      num(cx - k) + " " + num(cy + r) + " " + num(cx - r) + " " + num(cy + k) + " " + num(cx - r) + " " + num(cy) + " c",
      num(cx - r) + " " + num(cy - k) + " " + num(cx - k) + " " + num(cy - r) + " " + num(cx) + " " + num(cy - r) + " c",
      num(cx + k) + " " + num(cy - r) + " " + num(cx + r) + " " + num(cy - k) + " " + num(cx + r) + " " + num(cy) + " c",
      "f"
    ].join("\n");
  }

  /* CNC anchor marks: white square + black dot at each position.
     positions are [xIn, yIn] measured from the TOP-LEFT of the sheet. */
  function anchorMarkOps(marks, W, H) {
    if (!marks || !marks.enabled || !marks.positions || !marks.positions.length) return "";
    var sq = (marks.squareIn || 0.451) * PT;
    var r = ((marks.dotIn || 0.25) * PT) / 2;
    var ops = "";
    marks.positions.forEach(function (p) {
      var cx = p[0] * PT;
      var cy = H - p[1] * PT; /* top-left inches -> PDF bottom-left points */
      ops += "1 1 1 rg\n" +
             num(cx - sq / 2) + " " + num(cy - sq / 2) + " " + num(sq) + " " + num(sq) + " re\nf\n" +
             "0 0 0 rg\n" + circleOps(cx, cy, r) + "\n";
    });
    return ops;
  }

  /* Crop ticks at the trim corners (like the production file): 27pt long,
     6pt gap outside the trim box, thin black strokes. */
  function cropTickOps(W, H, b) {
    var L = 27, G = 6;
    var x0 = b, x1 = W - b, y0 = b, y1 = H - b;
    var seg = function (ax, ay, bx, by) {
      return num(ax) + " " + num(ay) + " m " + num(bx) + " " + num(by) + " l\n";
    };
    var ops = "0 0 0 RG\n0.5 w\n";
    /* horizontal ticks */
    ops += seg(x0 - G, y1, x0 - G - L, y1) + seg(x1 + G, y1, x1 + G + L, y1);
    ops += seg(x0 - G, y0, x0 - G - L, y0) + seg(x1 + G, y0, x1 + G + L, y0);
    /* vertical ticks */
    ops += seg(x0, y1 + G, x0, y1 + G + L) + seg(x1, y1 + G, x1, y1 + G + L);
    ops += seg(x0, y0 - G, x0, y0 - G - L) + seg(x1, y0 - G, x1, y0 - G - L);
    ops += "S\n";
    return ops;
  }

  /* opts:
     {
       trimWIn, trimHIn, bleedIn,
       pages: [{ jpeg: Uint8Array, pxW, pxH }],   // one per printed side
       marks: { enabled, squareIn, dotIn, positions: [[xIn, yIn from top-left]] },
       cropTicks: true,
       title
     }                                                                     */
  function build(opts) {
    var W = (opts.trimWIn + 2 * opts.bleedIn) * PT;
    var H = (opts.trimHIn + 2 * opts.bleedIn) * PT;
    var b = opts.bleedIn * PT;

    var overlayOps = anchorMarkOps(opts.marks, W, H);
    if (opts.cropTicks !== false && b >= 33 / PT * PT) overlayOps += cropTickOps(W, H, b);

    /* ── Assemble objects ─────────────────────────────────────────── */
    var objects = [];
    function addObj(bodyStr) {
      var n = objects.length + 1;
      objects.push(enc(n + " 0 obj\n" + bodyStr + "\nendobj\n"));
      return n;
    }
    function addStreamObj(dictStr, bytes) {
      var n = objects.length + 1;
      var head = enc(n + " 0 obj\n<< " + dictStr + " /Length " + bytes.length + " >>\nstream\n");
      var tail = enc("\nendstream\nendobj\n");
      var out = new Uint8Array(head.length + bytes.length + tail.length);
      out.set(head, 0); out.set(bytes, head.length); out.set(tail, head.length + bytes.length);
      objects.push(out);
      return n;
    }

    /* Reserve 1 = Catalog, 2 = Pages, 3 = Info (bodies filled below). */
    addObj("PLACEHOLDER-CATALOG");
    addObj("PLACEHOLDER-PAGES");
    var title = (opts.title || "Church Sign Shop print file").replace(/[()\\]/g, " ");
    /* the full design JSON rides in /Subject so any order can be re-rendered
       from its print file alone (keeps the Shopify order view clean) */
    var subject = (opts.subject || "").replace(/[()\\]/g, " ");
    addObj("<< /Title (" + title + ")" +
           (subject ? " /Subject (" + subject + ")" : "") +
           " /Producer (Church Sign Shop configurator) >>");

    var pageRefs = [];
    opts.pages.forEach(function (pg) {
      var imgN = addStreamObj(
        "/Type /XObject /Subtype /Image /Width " + pg.pxW + " /Height " + pg.pxH +
        " /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode",
        pg.jpeg
      );
      var content = "q\n" + num(W) + " 0 0 " + num(H) + " 0 0 cm\n/Im0 Do\nQ\n" + overlayOps;
      var contentBytes = enc(content);
      var contN = addStreamObj("", contentBytes);
      var pageN = addObj(
        "<< /Type /Page /Parent 2 0 R" +
        " /MediaBox [0 0 " + num(W) + " " + num(H) + "]" +
        " /TrimBox [" + num(b) + " " + num(b) + " " + num(W - b) + " " + num(H - b) + "]" +
        " /BleedBox [0 0 " + num(W) + " " + num(H) + "]" +
        " /Resources << /XObject << /Im0 " + imgN + " 0 R >> >>" +
        " /Contents " + contN + " 0 R >>"
      );
      pageRefs.push(pageN + " 0 R");
    });

    objects[0] = enc("1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n");
    objects[1] = enc("2 0 obj\n<< /Type /Pages /Kids [" + pageRefs.join(" ") +
                     "] /Count " + pageRefs.length + " >>\nendobj\n");

    /* ── Serialize with xref ──────────────────────────────────────── */
    var header = enc("%PDF-1.4\n%\xE2\xE3\xCF\xD3\n");
    var parts = [header];
    var offsets = [];
    var pos = header.length;
    objects.forEach(function (o) {
      offsets.push(pos);
      parts.push(o);
      pos += o.length;
    });
    var xrefStart = pos;
    var xref = "xref\n0 " + (objects.length + 1) + "\n0000000000 65535 f \n";
    offsets.forEach(function (o) {
      xref += ("0000000000" + o).slice(-10) + " 00000 n \n";
    });
    xref += "trailer\n<< /Size " + (objects.length + 1) +
            " /Root 1 0 R /Info 3 0 R >>\nstartxref\n" + xrefStart + "\n%%EOF\n";
    parts.push(enc(xref));

    return new Blob(parts, { type: "application/pdf" });
  }

  window.SignPDF = { build: build };
})();
